import { useState } from "react";
import { useApp } from "../context/AppContext";
import { SavedTeam, Tier } from "../types/pokemon";
import { formatTeamForShowdown, copyToClipboard } from "../utils/showdown";
import { translateType, getTypeColor } from "../utils/typeUtils";
import { showToast } from "../components/Toast";

export function CollectionScreen() {
  const { state, dispatch } = useApp();
  const { savedTeams } = state;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleBack = () => {
    dispatch({ type: "SET_SCREEN", screen: "home" });
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Supprimer cette equipe ?")) {
      dispatch({ type: "DELETE_SAVED_TEAM", id });
      showToast("Equipe supprimee", "success");
    }
  };

  const handleExport = async (team: SavedTeam, tier: Tier = "ou") => {
    const text = formatTeamForShowdown(team.team, tier);
    await copyToClipboard(text);
    showToast("Equipe copiee pour Showdown !", "success");
  };

  // Stats
  const totalTeams = savedTeams.length;
  const allPokemon = savedTeams.flatMap(t => t.team);
  const pokemonCounts = allPokemon.reduce<Record<string, { name: string; count: number }>>((acc, p) => {
    acc[p.id] = acc[p.id] || { name: p.nameFr, count: 0 };
    acc[p.id].count++;
    return acc;
  }, {});
  const mostPicked = Object.values(pokemonCounts).sort((a, b) => b.count - a.count)[0];
  const genCounts = allPokemon.reduce<Record<number, number>>((acc, p) => {
    acc[p.generation] = (acc[p.generation] || 0) + 1;
    return acc;
  }, {});
  const favoriteGen = Object.entries(genCounts).sort(([, a], [, b]) => b - a)[0]?.[0];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <button
          onClick={handleBack}
          className="font-pokemon text-xs text-white/70 hover:text-white transition-colors"
        >
          ← Retour
        </button>
        <h1 className="font-pokemon text-lg text-poke-yellow">Mes equipes</h1>
        <div className="w-16" />
      </div>

      {/* Stats bar */}
      {totalTeams > 0 && (
        <div className="flex flex-wrap gap-4 justify-center p-4 bg-gray-800/50 border-b border-gray-700">
          <StatBadge label="Equipes" value={String(totalTeams)} />
          {mostPicked && (
            <StatBadge label="Plus choisi" value={mostPicked.name} />
          )}
          {favoriteGen && (
            <StatBadge label="Gen favorite" value={`Gen ${favoriteGen}`} />
          )}
        </div>
      )}

      {/* Team list */}
      <div className="flex-1 p-4 max-w-4xl mx-auto w-full">
        {savedTeams.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-white/50">
            <div className="text-4xl mb-4">📦</div>
            <p className="font-pokemon text-sm">Aucune equipe sauvegardee</p>
            <p className="text-xs mt-2">Sauvegardez vos equipes depuis l'ecran de bataille !</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {savedTeams.map((saved) => (
              <TeamCard
                key={saved.id}
                saved={saved}
                expanded={expandedId === saved.id}
                onToggle={() => setExpandedId(expandedId === saved.id ? null : saved.id)}
                onDelete={() => handleDelete(saved.id)}
                onExport={() => handleExport(saved)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center bg-gray-700/50 rounded-lg px-4 py-2">
      <span className="text-[10px] text-white/50 uppercase tracking-wider">{label}</span>
      <span className="font-pokemon text-xs text-poke-yellow">{value}</span>
    </div>
  );
}

interface TeamCardProps {
  saved: SavedTeam;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onExport: () => void;
}

function TeamCard({ saved, expanded, onToggle, onDelete, onExport }: TeamCardProps) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden transition-all">
      {/* Summary row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 hover:bg-gray-700/50 transition-colors text-left"
      >
        {/* Sprites preview */}
        <div className="flex -space-x-2 shrink-0">
          {saved.team.slice(0, 6).map((p) => (
            <img
              key={p.id}
              src={p.sprite}
              alt={p.nameFr}
              className="w-10 h-10 bg-gray-700 rounded-full border-2 border-gray-600"
              style={{ imageRendering: "pixelated" }}
            />
          ))}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-pokemon text-xs text-white truncate">{saved.name}</div>
          <div className="text-[10px] text-white/40">{saved.date}</div>
        </div>

        <span className="text-white/30 text-lg">{expanded ? "▲" : "▼"}</span>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-700 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {saved.team.map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-gray-700/50 rounded-lg p-2">
                <img
                  src={p.sprite}
                  alt={p.nameFr}
                  className="w-12 h-12"
                  style={{ imageRendering: "pixelated" }}
                />
                <div>
                  <div className="font-retro text-[10px] text-white">{p.nameFr}</div>
                  <div className="flex gap-1 mt-1">
                    {p.types.map((type) => (
                      <span
                        key={type}
                        className="px-1.5 py-0.5 rounded text-[8px] text-white font-bold uppercase"
                        style={{ backgroundColor: getTypeColor(type) }}
                      >
                        {translateType(type)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={onExport}
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-xs px-4 py-2 rounded-lg transition-colors"
            >
              Exporter vers Showdown
            </button>
            <button
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-4 py-2 rounded-lg transition-colors"
            >
              Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
